// services/facebook.service.ts
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  catchError,
  from,
  map,
  Observable,
  of,
  switchMap,
  throwError
} from 'rxjs';

import {
  AuthenticationResponseDto,
  FacebookAuth,
  SocialLoginProvider,
  UserRole
} from '../../../models/auth.model';
import { CustomError } from '../../../models/custom-error.model';
import { AUTH_ERROR } from '../auth-errors.model';

@Injectable({
  providedIn: 'root'
})
export class FacebookHandlerService {
  private readonly baseUrl = '/api/Auth/social-login';
  private http = inject(HttpClient);
  private userRole?: UserRole;

  /**
   * Initiates the Facebook authentication flow.
   *
   * @param userRole - The role of the user attempting to authenticate.
   * @returns An Observable that emits when the authentication process completes.
   *
   * @throws {CustomError} When the user denies provider access or the provider fails.
   */
  initiateAuthFlow(
    userRole: UserRole
  ): Observable<AuthenticationResponseDto | null> {
    this.userRole = userRole;

    return from(this.loginWithFacebook()).pipe(
      map((response) => {
        return response.authResponse.accessToken;
      }),
      switchMap((accessToken) => {
        // Call handleCallback only when login is successful
        return this.handleCallback(accessToken);
      }),
      catchError((error) => {
        if (error.status !== 'connected') {
          throw new CustomError(AUTH_ERROR.PROVIDER_DENIED, {
            ...error,
            provider: SocialLoginProvider.Facebook
          });
        }

        return throwError(
          () =>
            new CustomError(AUTH_ERROR.PROVIDER_FAILED, {
              ...error,
              provider: SocialLoginProvider.Facebook
            })
        );
      })
    );
  }

  private handleCallback(
    accessToken: string
  ): Observable<AuthenticationResponseDto | null> {
    return this.http
      .post<AuthenticationResponseDto>(this.baseUrl, {
        accessToken,
        provider: SocialLoginProvider.Facebook,
        role: this.userRole
      })
      .pipe(
        catchError(() => {
          return of(null);
        })
      );
  }

  /**
   * Prompts user to login with Facebook.
   * @returns A promise that resolves with the authResponse (contains accessToken, userID, etc.)
   */
  loginWithFacebook(): Promise<FacebookAuth> {
    return new Promise((resolve, reject) => {
      window.FB.login(
        (response: FacebookAuth) => {
          if (response.status === 'connected') {
            resolve(response);
          } else {
            reject(response);
          }
        },
        { scope: 'public_profile,email', return_scopes: true }
      );
    });
  }

  /**
   * Gets current login status of Facebook user.
   * @returns A promise that resolves with the login status response.
   */
  getLoginStatus(): Promise<unknown> {
    return new Promise((resolve) => {
      window.FB.getLoginStatus((response: unknown) => {
        resolve(response);
      });
    });
  }

  /**
   * Optional: Log out of Facebook.
   * @returns A promise that resolves when logout is done.
   */
  logout(): Promise<void> {
    return new Promise((resolve) => {
      window.FB.logout(() => {
        resolve();
      });
    });
  }
}
